// TODO
// -
//
// MOMENT TIMEZONE NOTES
// - we can't use moment.tz.names() because moment and php appear to take a different aproach to compiling the IANA OLSEN DB for distribution
// - moment-timezone uses all available timezones in the db
// - php timezone compiles against either the zone.tab or zone1970.tab likely depending on the version
// - (zone1970 appears to have been created to supersede zone.tab on 2018-06-17).
// - moment thus contains all entries php does, so we can use a list generated by php for now
//
// - CLAMPING
// - No clamping is performed on incoming from/to props. They are displayed as received.
// - Min and max values are clamped to startOf/endOf days to ease UI display.
// - Value clamping is performed at onChange before passing back out
import { get } from "lodash";
import moment from "moment-timezone";
import React, { PureComponent } from "react";
import { connect } from "react-redux";
import { PropTypes } from "prop-types";
import { Form } from "semantic-ui-react";

import DatePicker from "react-datepicker";
import FormikAPIErrorMessage from "../../forms/formik/FormikAPIErrorMessage.jsx";

import { getErrorStateFromPropsByName } from "../formUtils.js";
import { selectCopy } from "../../../state/reducers/locale/copySelector.js";

class FormikComplexDateRange extends PureComponent {
  static propTypes = {
    form: PropTypes.object.isRequired,
    field: PropTypes.object.isRequired,
    readOnly: PropTypes.bool.isRequired,
    disabled: PropTypes.bool.isRequired,
    min: PropTypes.string,
    max: PropTypes.string,
    aggressiveClamping: PropTypes.bool.isRequired,
    apiErrors: PropTypes.object,
    copy: PropTypes.func.isRequired
  };

  static defaultProps = {
    aggressiveClamping: false,
    readOnly: false,
    disabled: false
  };

  constructor(props) {
    super(props);

    this.state = {
      timezone: "UTC" // fallback to UTC time if no timezone is set
    };
  }

  static getDerivedStateFromProps(props, state) {
    const timezone = get(props, "form.values.timezone.value", "UTC"); // fallback to UTC time if no timezone is set

    if (timezone !== state.timezone) {
      return {
        timezone
      };
    }

    return null;
  }

  componentDidMount() {
    const hydrationData = this.calculateValues();
    this.onFromChange(hydrationData._from);
    this.onToChange(hydrationData._to);
  }

  // -- DATA MANAGEMENT -- //

  calculateValues = () => {
    // console.log("calculateValues:");
    const {
      form: {
        values: { startAt, endAt }
      },
      min,
      max,
      aggressiveClamping
    } = this.props;
    const { timezone } = this.state;

    const _now = timezone ? moment().tz(timezone) : moment();

    // passed values should be displayed as passed not mutated to fit component clamping rules
    // clamping rules will be applied to outgoing values - see handlers
    const _from = startAt ? moment.tz(startAt, timezone) : null;
    const _to = endAt ? moment.tz(endAt, timezone) : null;

    const _max = max ? moment.tz(max, timezone).endOf("day") : null;
    const _min = min ? moment.tz(min, timezone).startOf("day") : null;

    // clamp the from calendar
    const _fromMin = _min ? _min.clone() : null;
    let _fromMax = _max ? _max.clone() : null;
    _fromMax = _to && aggressiveClamping ? _to.clone() : _fromMax;

    // clamp the to calendar
    const _toMax = _max ? _max.clone() : null;
    let _toMin = _min ? _min.clone() : null;
    _toMin = _from ? _from.clone() : _toMin;

    return {
      _now,
      _from,
      _to,
      _min,
      _max,
      _toMin,
      _toMax,
      _fromMin,
      _fromMax
    };
  };

  // -- HANDLERS -- //

  onFromChange = date => {
    // console.log("onFromChange:", date);
    const { form, aggressiveClamping } = this.props;
    const { timezone } = this.state;
    const { _to, _fromMin, _fromMax } = this.calculateValues();

    if (date) {
      if (timezone) date.tz(timezone);
      date.startOf("day");

      // set from to the start of the day based on selected timezone
      // clamp to max and min value
      // clamp so to can't be greater than to
      if (date) {
        date =
          _fromMax && date.unix() > _fromMax.unix() ? _fromMax.clone() : date;
        date =
          _fromMin && date.unix() < _fromMin.unix() ? _fromMin.clone() : date;
        // will mean that to has to be changed before from can be changed
        // otherwise to will advance based on how from is set
        if (aggressiveClamping) {
          date = _to && date.unix() > _to.unix() ? _to.clone() : date;
        }
      }
    }

    form.setFieldValue("startAt", date ? date.toISOString() : null);
  };

  onToChange = date => {
    // console.log("onToChange:", date);
    const { form } = this.props;
    const { timezone } = this.state;
    const { _from, _toMin, _toMax } = this.calculateValues();

    if (date) {
      if (timezone) date.tz(timezone);
      date.endOf("day");

      // set to to the end of the day based on selected timezone
      // clamp to max and min value
      // clamp so to can't be less than from
      date = _toMax && date.unix() > _toMax.unix() ? _toMax.clone() : date;
      date = _toMin && date.unix() < _toMin.unix() ? _toMin.clone() : date;
      date = _from && date.unix() < _from.unix() ? _from.clone() : date;
    }

    form.setFieldValue("endAt", date ? date.toISOString() : null);
  };

  onFromBlur = () => {
    this.props.form.setFieldTouched("startAt", true);
  };

  onToBlur = () => {
    this.props.form.setFieldTouched("endAt", true);
  };

  render() {
    const {
      field,
      readOnly,
      disabled,
      required = false,
      apiErrors,
      copy
    } = this.props;

    const { _from, _to, _toMin, _toMax, _fromMin, _fromMax } =
      this.calculateValues();

    const fromLabel = copy("forms.complex_date_range.start_label");
    const toLabel = copy("forms.complex_date_range.end_label");

    // set semantic-ui field error state
    const isFromError = getErrorStateFromPropsByName("startAt", this.props);
    const isToError = getErrorStateFromPropsByName("endAt", this.props);
    const isRequired = readOnly || disabled ? false : required;
    const dateFormat = "DD MMM YYYY";

    // set date to current month by default
    if (!readOnly) {
      return (
        <Form.Group widths={3}>
          <Form.Field required={isRequired} error={isFromError}>
            {fromLabel && <label>{fromLabel || null}</label>}
            <DatePicker
              // dateFormat="DD MMM YYYY HH:mm" // added time for debug
              dateFormat={dateFormat}
              popperClassName="usagePopper"
              placeholderText={copy(
                "forms.complex_date_range.start_placeholder"
              )}
              shouldCloseOnSelect={true}
              selectsStart
              minDate={_fromMin}
              maxDate={_fromMax}
              selected={_from}
              startDate={_from}
              endDate={_to}
              readOnly={readOnly}
              disabled={disabled}
              onChange={this.onFromChange}
              onBlur={this.onFromBlur}
            />
            <FormikAPIErrorMessage name={"startAt"} apiErrors={apiErrors} />
          </Form.Field>
          <Form.Field error={isToError}>
            {toLabel && <label>{toLabel || null}</label>}
            <DatePicker
              // dateFormat="DD MMM YYYY HH:mm" // added time for debug
              dateFormat={dateFormat}
              popperClassName="usagePopper"
              placeholderText={copy("forms.complex_date_range.end_placeholder")}
              shouldCloseOnSelect={true}
              selectsEnd
              isClearable={true}
              minDate={_toMin}
              maxDate={_toMax}
              selected={_to}
              startDate={_from}
              endDate={_to}
              readOnly={readOnly}
              disabled={disabled}
              onChange={this.onToChange}
              onBlur={this.onToBlur}
            />
            <FormikAPIErrorMessage name={"endAt"} apiErrors={apiErrors} />
          </Form.Field>
        </Form.Group>
      );
    }

    return (
      <Form.Group widths={3}>
        {_from && (
          <Form.Field required={isRequired} error={isFromError}>
            {fromLabel && <label>{fromLabel || null}</label>}
            <p>{_from.format(dateFormat)}</p>
          </Form.Field>
        )}
        {_to && (
          <Form.Field error={isToError}>
            {toLabel && <label>{toLabel || null}</label>}
            <p>{_to.format(dateFormat)}</p>
          </Form.Field>
        )}
      </Form.Group>
    );
  }
}

const mapStateToProps = state => ({
  copy: selectCopy(state)
});

export default connect(mapStateToProps)(FormikComplexDateRange);
